import type { Session, User } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export function useAuth() {
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Get initial session
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session);
            setUser(session?.user ?? null);
            setLoading(false);
        });

        // Listen for auth changes
        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session);
            setUser(session?.user ?? null);
            setLoading(false);
        });

        return () => subscription.unsubscribe();
    }, []);

    const signInWithGoogle = async () => {
        // Get the correct redirect URL based on environment
        const getRedirectUrl = () => {
            // Use environment variable if available
            const envRedirectUrl = import.meta.env.VITE_AUTH_REDIRECT_URL;
            if (envRedirectUrl) {
                return envRedirectUrl;
            }

            // Fallback to current origin + callback path
            return `${window.location.origin}/auth/callback`;
        };

        const { data, error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: getRedirectUrl(),
            },
        });

        if (error) {
            console.error('Error signing in with Google:', error);
            throw error;
        }

        return data;
    };

    const signInWithPassword = async (email: string, password: string) => {
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });

        if (error) {
            console.error('Error signing in with password:', error);
            throw error;
        }

        return data;
    };

    const signOut = async () => {
        const { error } = await supabase.auth.signOut();
        if (error) {
            console.error('Error signing out:', error);
            throw error;
        }
    };

    return {
        user,
        session,
        loading,
        signInWithGoogle,
        signInWithPassword,
        signOut,
    };
}
